import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { LiveToggle } from "@/components/LiveToggle";
import { useLiveSource } from "@/hooks/useLiveSource";
import { toast } from "sonner";
import {
  Wallet, CreditCard, Loader2, Search, CheckCircle2, Clock, XCircle,
  ShieldAlert, ShieldCheck, Play, RefreshCw, Eye,
} from "lucide-react";

const STATUS_COLORS = {
  success: "text-emerald-700 border-emerald-300 bg-emerald-50",
  credited: "text-emerald-700 border-emerald-300 bg-emerald-50",
  pending: "text-amber-700 border-amber-300 bg-amber-50",
  initiated: "text-amber-700 border-amber-300 bg-amber-50",
  verified: "text-blue-700 border-blue-300 bg-blue-50",
  pending_paymode_approval: "text-orange-700 border-orange-300 bg-orange-50",
  paymode_rejected: "text-red-700 border-red-300 bg-red-50",
  failed: "text-red-700 border-red-300 bg-red-50",
};

const SECTION_TABS = [
  { key: "all", label: "All Wallet Txns", icon: Wallet },
  { key: "pending_credit", label: "Pending Credit (Paymode Held)", icon: ShieldAlert },
];

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return "—"; }
};

export default function AdminWalletTxns() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("all");
  const [search, setSearch] = useState("");
  const [sinceHours, setSinceHours] = useState(168);
  const [walletType, setWalletType] = useState("all");
  const [processing, setProcessing] = useState(false);
  const { source, setSource, relayConfigured, isPreview } = useLiveSource();

  const load = async () => {
    setLoading(true);
    try {
      if (source === "live") {
        const tables = walletType === "partner" ? ["wallet_topups"] : walletType === "customer" ? ["customer_wallet_topups"] : ["wallet_topups", "customer_wallet_topups"];
        const allRows = [];
        for (const table of tables) {
          const { data } = await api.post("/admin/system-test/live-db", {
            table, filters: {}, sort_field: "created_at", sort_dir: -1, limit: 250,
          });
          if (data.rows) {
            data.rows.forEach(r => allRows.push({
              ...r, id: r.id,
              kind: table === "wallet_topups" ? "partner_topup" : "customer_topup",
              amount: parseFloat(r.amount || 0),
              status_bucket: ["success", "credited"].includes(r.status) ? "success" : ["failed", "paymode_rejected"].includes(r.status) ? "failed" : "pending",
              target_wallet: table === "wallet_topups" ? "partner" : "customer",
              target_label: table === "wallet_topups" ? (r.user_id || "") : (r.mobile || ""),
              created_by_label: r.partner_id || r.user_id || "",
              wallet_credited: r.wallet_credited || false,
              credit_status: r.credit_status || "",
              table,
            }));
          }
        }
        allRows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setRows(allRows);
      } else {
        const kind = walletType === "partner" ? "partner_topup" : walletType === "customer" ? "customer_topup" : "all";
        const paymodeFilter = section === "pending_credit" ? "non_whitelisted" : "all";
        const params = { kind: kind === "all" ? "all" : kind, status: "all", since_hours: sinceHours, limit: 500, paymode_filter: paymodeFilter };
        if (search.trim()) params.search = search.trim();
        const { data } = await api.get("/outbound-txns", { params });
        let items = data.items || [];
        if (kind === "all") items = items.filter(r => r.kind === "partner_topup" || r.kind === "customer_topup");
        setRows(items);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [section, sinceHours, walletType, source]);

  const stats = useMemo(() => {
    let partnerCount = 0, customerCount = 0, partnerAmt = 0, customerAmt = 0;
    let heldCount = 0, heldAmt = 0;
    rows.forEach(r => {
      if (r.kind === "partner_topup") { partnerCount++; partnerAmt += r.amount || 0; }
      if (r.kind === "customer_topup") { customerCount++; customerAmt += r.amount || 0; }
      if (r.credit_status === "pending_paymode_approval" || r.credit_status === "paymode_rejected") {
        heldCount++; heldAmt += r.amount || 0;
      }
    });
    return { partnerCount, customerCount, partnerAmt, customerAmt, heldCount, heldAmt, total: rows.length };
  }, [rows]);

  const processApproved = async () => {
    if (!window.confirm("Process ALL topups whose paymode has been approved? This will credit the wallets.")) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/outbound-txns/process-approved");
      if (data.released_count > 0) {
        toast.success(data.message);
      } else {
        toast.info(data.message);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Process failed");
    } finally { setProcessing(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-wallet-txns">
        <div>
          <span className="overline">ADMIN</span>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2">Wallet Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            View all partner and customer wallet top-up transactions. The "Pending Credit" tab shows topups
            held for paymode approval — approve the paymode first, then process the credits.
          </p>
          <div className="mt-2">
            <LiveToggle source={source} setSource={setSource} relayConfigured={relayConfigured} isPreview={isPreview} />
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-2 flex-wrap" data-testid="wallet-section-tabs">
          {SECTION_TABS.map(({ key, label, icon: TabIcon }) => (
            <Button
              key={key}
              size="sm"
              variant={section === key ? "default" : "outline"}
              onClick={() => setSection(key)}
              className="gap-1.5"
              data-testid={`wallet-tab-${key}`}
            >
              <TabIcon className="h-3.5 w-3.5" /> {label}
              {key === "pending_credit" && stats.heldCount > 0 && section !== key && (
                <span className="ml-1 bg-red-100 text-red-700 text-[10px] px-1.5 py-0.5 rounded-full">{stats.heldCount}</span>
              )}
            </Button>
          ))}
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="surface-card p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Partner Topups</div>
            <div className="text-2xl font-display font-bold mt-1">{stats.partnerCount}</div>
            <div className="text-xs font-mono text-muted-foreground">{formatINR(stats.partnerAmt)}</div>
          </div>
          <div className="surface-card p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Customer Topups</div>
            <div className="text-2xl font-display font-bold mt-1">{stats.customerCount}</div>
            <div className="text-xs font-mono text-muted-foreground">{formatINR(stats.customerAmt)}</div>
          </div>
          <div className="surface-card p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Total</div>
            <div className="text-2xl font-display font-bold mt-1">{stats.total}</div>
            <div className="text-xs font-mono text-muted-foreground">{formatINR(stats.partnerAmt + stats.customerAmt)}</div>
          </div>
          <div className="surface-card p-4 border-l-4 border-l-amber-500">
            <div className="text-xs uppercase tracking-widest text-amber-700">Held for Paymode</div>
            <div className="text-2xl font-display font-bold mt-1 text-amber-700">{stats.heldCount}</div>
            <div className="text-xs font-mono text-amber-600">{formatINR(stats.heldAmt)}</div>
          </div>
        </div>

        {/* Pending credit banner */}
        {section === "pending_credit" && rows.length > 0 && (
          <div className="surface-card border-l-4 border-l-amber-500 p-4 flex items-center justify-between" data-testid="pending-credit-banner">
            <div>
              <div className="font-semibold text-amber-800">{rows.length} topup{rows.length > 1 ? "s" : ""} held for paymode approval</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Approve the payment mode in the Whitelist page first, then click "Process Approved" to credit wallets.
              </div>
            </div>
            <Button onClick={processApproved} disabled={processing} className="gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="process-approved-wallet-btn">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Process Approved
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="surface-card p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">Search</label>
            <div className="flex gap-1 mt-1">
              <Input value={search} onChange={(e)=>setSearch(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()}
                placeholder="txnid, mobile, provider ref…" data-testid="wallet-search" />
              <Button variant="outline" onClick={load}><Search className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="min-w-[140px]">
            <label className="text-xs text-muted-foreground">Wallet type</label>
            <Select value={walletType} onValueChange={setWalletType}>
              <SelectTrigger className="mt-1" data-testid="filter-wallet-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <label className="text-xs text-muted-foreground">Window</label>
            <Select value={String(sinceHours)} onValueChange={(v)=>setSinceHours(Number(v))}>
              <SelectTrigger className="mt-1" data-testid="filter-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24">Last 24h</SelectItem>
                <SelectItem value="72">Last 3 days</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
                <SelectItem value="720">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} className="gap-1.5" data-testid="wallet-refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>

        {/* Table */}
        <div className="surface-card overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              {section === "pending_credit" ? "No topups held for paymode approval." : "No wallet transactions in this window."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-accent/50 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Credit Status</th>
                  <th className="px-3 py-2 text-left">Target</th>
                  <th className="px-3 py-2 text-left">Partner</th>
                  <th className="px-3 py-2 text-left">Gateway</th>
                  <th className="px-3 py-2 text-left">Credited</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const cs = r.credit_status || "";
                  const statusColor = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
                  const creditColor = STATUS_COLORS[cs] || "";
                  return (
                    <tr key={`${r.table}-${r.id}`} className="hover:bg-accent/30" data-testid={`wallet-row-${r.id}`}>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtIST(r.created_at)}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          {r.kind === "partner_topup" ? <Wallet className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}
                          {r.kind === "partner_topup" ? "Partner" : "Customer"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{formatINR(r.amount)}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${statusColor}`}>{r.status}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        {cs ? (
                          <Badge variant="outline" className={`text-[10px] ${creditColor}`}>
                            {cs === "pending_paymode_approval" && <ShieldAlert className="h-3 w-3 mr-1" />}
                            {cs === "paymode_rejected" && <XCircle className="h-3 w-3 mr-1" />}
                            {cs === "credited" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                            {cs.replace(/_/g, " ")}
                          </Badge>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">{r.target_label || "—"}</td>
                      <td className="px-3 py-2 text-xs">{r.created_by_label || "—"}</td>
                      <td className="px-3 py-2 text-xs">{r.gateway || "—"}</td>
                      <td className="px-3 py-2">
                        {r.wallet_credited
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          : <Clock className="h-4 w-4 text-amber-500" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
